import type { LicenseData as FitmentLicenseData, TireSize as FitmentTireSize } from "./tireselector-v2/fitment-api";

export type TireSize = FitmentTireSize;
export type License = FitmentLicenseData;

export type Vehicle = {
  /* Manufacturer ID, slugified name */
  manufacturer: string;
  /* Manufacturer name */
  manufacturerName: string | null;
  /* Model ID, numeric string */
  model: string;
  /* Model name */
  modelName: string | null;
  /* Manufacturing year, numeric string */
  year: string;
  /* Vehicle (engine) ID, numeric string */
  vehicle: string | null;
  /* Vehicle (engine) name */
  vehicleName: string | null;
};

export type VehicleNA = {
  /* Manufacturing year, numeric string */
  year: string;
  /* Manufacturer ID and cleartext name */
  manufacturer: string;
  /* Model ID and cleartext name */
  model: string;
  /* Submodel ID, numeric id (consists of two ids joined by pipe, e.g. "183465|93053") */
  submodel: string;
  /* Submodel name */
  submodelName: string | null;
}

export type Location = {
  /* Two letter country code, e.g. FI, SE */
  countryCode: string;
  /* Numeric subdivision code, e.g. 18 for Uusimaa in Finland */
  subdivisionCode: string;
}

export type GeoSeason = 'summer' | 'winter' | 'all_season' | 'all_weather' | 'studded' | 'non_studded';

export type ProductSize = {
  /** T-code */
  code: string;
  /** Size string, e.g. "215/60 R 16 99V XL" */
  info: string;
}

export type SelectedProduct = {
  /* Item family code */
  code: string;
  /* Product name */
  name: string;
  /* Product image URL */
  image: string | null;
  /* Product large image URL */
  imageLarge: string | null;
  /* Product page URL */
  url: string;
  /** Suitable sizes available of this product */
  sizes: ProductSize[];
  selectedSize: ProductSize | null;
}

export type UserData = {
  /* Tyre season based on user's location */
  geoSeason: GeoSeason | null;
  /* User's location information */
  location: Location | null;
  /* User's vehicle license information (from tireselector) */
  license: License | null;
  /* User's vehicle information (from tireselector) */
  vehicle: Vehicle | null;
  vehicleNA: VehicleNA | null;
  /* User's selected tire size (general size from tireselector or other places, like 205/55R16. Not t-code level) */
  selectedSize: TireSize | null;
  /* Available tire sizes for the user's vehicle (from tireselector) */
  availableSizes: TireSize[] | null;
  /** User's selected product  */
  selectedProduct: SelectedProduct | null;
}

// Subscribers can listen to changes in user data per event type (key in userData object) or to change in any values.
export type UserDataEventType = keyof UserData | 'any';

// Data is either the entire user data object or specific value based on event type.
export type SubscriberFunction = (data: any) => void;

/**
 * This class stores all user related data in session storage and notifies subscribers on changes.
 *
 * Example of usage:

   // Subscribe to any changes in userData object
   userData.subscribe('any', (newData) => {
    console.log('User data changed:', newData);
   });

   // Subscribe to changes in selectedSize only
   userData.subscribe('selectedSize', (newSize) => {
     console.log('User selected size changed:', newSize);
   });

   // Get current user data, entire object
   const currentData = userData.get();

   // Get current selected size only
   const currentSize = userData.get('selectedSize');

   // Store new data (can be partial update)
   // Subscribers of 'any', 'selectedSize', and 'geoSeason' events will be notified
   userData.store({
     selectedSize: { width: 205, aspectRatio: 55, diameter: 16 },
     geoSeason: 'winter',
   });

 */
class User {
  #storageKey = 'userData';
  events: { [key in UserDataEventType]?: Set<SubscriberFunction> };
  data: UserData;

  constructor() {
    this.events = {};

    const defaultData = {
      geoSeason: null,
      location: null,
      license: null,
      vehicle: null,
      vehicleNA: null,
      selectedSize: null,
      availableSizes: null,
      selectedProduct: null,
    };

    try {
      const str = sessionStorage.getItem(this.#storageKey);
      this.data = str ? JSON.parse(str): defaultData;
    } catch(e) {
      this.data = defaultData;
    }
  }

  subscribe(event: UserDataEventType, subscriberFn: SubscriberFunction) {
    if (!this.events[event]) {
      this.events[event] = new Set();
    }
    this.events[event]!.add(subscriberFn);
  }

  unsubscribe(event: UserDataEventType, subscriberFn: SubscriberFunction) {
    if (this.events[event]) {
     this.events[event]!.delete(subscriberFn);
    }
  }

  #notify(event: UserDataEventType) {
    if (!this.events[event]) {
      // No subscribers for this event
      return;
    }

    this.events[event]!.forEach(subscriberFn => {
      if (event === 'any') {
        // Notify for any change
        subscriberFn(this.data);
      } else {
        // Notify with specific event data
        subscriberFn(this.data[event]);
      }
    });
  }

  store(dataToUpdate:Partial<UserData>) {
    const newData = {
      ...this.data,
      ...dataToUpdate,
    };

    console.log('UserData.store()', {dataToUpdate, oldData: this.data, newData});

    this.data = newData;
    sessionStorage.setItem(this.#storageKey, JSON.stringify(newData));

    // Notify subscribers of specific events by object keys
    Object.keys(dataToUpdate).forEach(key => {
      this.#notify(key as UserDataEventType);
    });

    // also notify those that subscribe to any change
    this.#notify('any');
  }

  // Overloads
  get(): UserData;
  get(event: 'any'): UserData;
  get<K extends keyof UserData>(event: K): UserData[K];

  get(event?: keyof UserData | 'any') {
    if (event === 'any' || event === undefined) {
      return this.data;
    } else if (event) {
      return this.data[event] || null;
    } else {
      console.error(`UserData.get() - Invalid event type: ${event}`, this.data);
      throw new Error(`UserData.get() - Invalid event type: ${event}`);
    }
  }
}

export default new User();
